import type { DetectedFinger } from './client';

/**
 * NFIQ fingerprint image quality.
 *
 * The Device Service reports NFIQ 2 per detected finger: 0–100, HIGHER IS BETTER (not NFIQ 1's 1–5
 * where lower is better — getting that backwards would colour every good print red). Older builds
 * returned the same number under a deprecated `nfiq2` property, which v1 fell back to; that fallback is
 * kept here because customers run older Device Service versions.
 *
 * Thresholds match v1 exactly (>=60 good, >=40 fair, below that poor) so an operator moving from v1 sees
 * the same print judged the same way.
 *
 * Quality is advisory ONLY. v1 interrupted a low-quality capture with a "Poor Quality" dialog; here a
 * low score never blocks or re-prompts — it is shown and the operator decides.
 */
export type QualityBand = 'good' | 'fair' | 'poor';

export const QUALITY_GOOD_MIN = 60;
export const QUALITY_FAIR_MIN = 40;

export function qualityBand(score: number): QualityBand {
  if (score >= QUALITY_GOOD_MIN) return 'good';
  if (score >= QUALITY_FAIR_MIN) return 'fair';
  return 'poor';
}

/**
 * Mean NFIQ across the fingers detected in one impression, rounded — a four-finger slap reports four
 * scores and the card shows one number, as in v1.
 *
 * Returns null when nothing usable came back, so "not measured" stays distinct from a genuine score:
 * a 0 would render as a poor-quality print that was never actually assessed.
 */
export function meanQuality(fingers: DetectedFinger[] | null | undefined): number | null {
  const scores = (fingers ?? [])
    .map((f) => f.quality ?? (f as { nfiq2?: number }).nfiq2)
    .filter((n): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0);
  if (!scores.length) return null;
  return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
}

/** Combine the two half-slap scores into the stitched slap's score, as v1 did. */
export function combineQuality(a: number | null | undefined, b: number | null | undefined): number | null {
  if (typeof a === 'number' && typeof b === 'number') return Math.round((a + b) / 2);
  return typeof a === 'number' ? a : typeof b === 'number' ? b : null;
}
